import { useEffect, useRef, useState, type ReactNode } from "react"
import { Routes, Route, useLocation, useNavigate } from "react-router-dom"
import {
  createBridge,
  createAppSession,
  type AppBridge,
  type AppClient,
  type AppContext,
  type AppSession,
} from "@koortex/apps-sdk"
import { fetchMe } from "@/lib/api"
import { AppHeader } from "@/components/AppHeader"
import { Home } from "@/pages/Home"
import { Launcher } from "@/pages/Launcher"
import { Reporting } from "@/pages/reporting/Reporting"
import { ProducaoArea } from "@/areas/producao"
import { ContractLink } from "@/areas/producao/ContractLink"
import { ArmazemArea } from "@/areas/armazem"
import { KNOWN_ROUTE_ROOTS, areaByKey, areaOfPath, type AreaKey } from "@/lib/areas"
import { List } from "@/pages/List"
import { Wizard } from "@/pages/solar/Wizard"
import { Detail } from "@/pages/solar/Detail"
import { Edit } from "@/pages/solar/Edit"
import { BcWizard } from "@/pages/bc/BcWizard"
import { BcDetail } from "@/pages/bc/BcDetail"
import { AcWizard } from "@/pages/ac/AcWizard"
import { AcDetail } from "@/pages/ac/AcDetail"
import { Settings } from "@/pages/Settings"
import { Mapeamentos } from "@/pages/Mapeamentos"
import { Ajuda } from "@/pages/Ajuda"

export interface AppShell {
  context: AppContext
  client: AppClient
  bridge: AppBridge
  /**
   * Numeric role from the host JWT. 10 = ADMIN, 5 = MANAGER, 3 = USER.
   * Decoded inline from the token (SDK strips unknown claims). Used to gate
   * admin-only UI (Settings, brand margins, category roles).
   */
  role: number
  /**
   * Admin pela lógica de CARGO (user_roles: Administrador/Master), resolvido via
   * /api/me — distinto do role numérico acima. Usado para ações restritas do
   * simulador (ex.: desconto). undefined enquanto resolve.
   */
  isAdmin?: boolean
  /**
   * Pode aplicar DESCONTO no Editar Kit: admin OU flag `allow_seller_discount`
   * ligado nas Configurações. O aumento de preço está sempre disponível.
   * undefined enquanto /api/me resolve.
   */
  canDiscount?: boolean
  /**
   * Áreas do menu inicial a que o utilizador tem acesso (equipas do Koortex,
   * resolvidas em /api/me). undefined enquanto resolve.
   */
  areas?: AreaKey[]
  /** Equipas do utilizador no Koortex. */
  teams?: Array<{ id: number | string; slug: string | null; name: string }>
}

/** Role mínimo para gerir o simulador (Manager+). Ver AppHeader (mesmo limiar). */
export const MANAGE_MIN_ROLE = 5

/**
 * Guarda de rota por role. Esconder o link no header não chega — sem isto um
 * User (role 3) podia abrir /mapeamentos ou /configuracoes pelo URL. Bloqueia
 * quando o role é conhecido e insuficiente; durante o load (shell null) deixa
 * a própria página tratar do estado.
 */
function RequireRole({
  shell,
  min,
  children,
}: {
  shell: AppShell | null
  min: number
  children: ReactNode
}) {
  if (shell && shell.role < min) {
    return (
      <div className="mx-auto max-w-md p-8 text-center">
        <h1 className="text-lg font-semibold">Acesso restrito</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Esta secção é reservada a gestores. Fala com um administrador se
          precisares de acesso.
        </p>
      </div>
    )
  }
  return <>{children}</>
}

/**
 * Guarda de rota por área (equipa do Koortex). O servidor volta a verificar
 * com `requireArea` — isto é só para não mostrar um ecrã que vai dar 403.
 * Durante o load (areas undefined) deixa a página tratar do estado.
 */
function RequireArea({
  shell,
  area,
  children,
}: {
  shell: AppShell | null
  area: AreaKey
  children: ReactNode
}) {
  if (shell?.areas && !shell.areas.includes(area)) {
    const label = areaByKey(area)?.label ?? area
    return (
      <div className="mx-auto max-w-md p-8 text-center">
        <h1 className="text-lg font-semibold">Sem acesso a {label}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          O acesso a esta área depende da tua equipa no Koortex. Fala com um
          administrador se precisares de acesso.
        </p>
      </div>
    )
  }
  return <>{children}</>
}

/**
 * O host abre sempre o iframe no `index.html` e passa o sub-caminho do URL do
 * CRM (`/apps/maisenergia/solar/123`) no contexto do token. Se for uma rota
 * que a app conhece, abre-a em vez do menu.
 */
function useHostDeepLink(shell: AppShell | null) {
  const navigate = useNavigate()
  const done = useRef(false)
  useEffect(() => {
    if (!shell || done.current) return
    done.current = true
    const path = String(shell.context.path ?? "").replace(/^\/+|\/+$/g, "")
    const root = path.split("/")[0] ?? ""
    if (path && KNOWN_ROUTE_ROOTS.has(root)) navigate(`/${path}`, { replace: true })
  }, [shell, navigate])
}

function decodeRoleFromToken(token: string): number {
  try {
    const body = token.split(".")[1]
    if (!body) return 3
    const normalized = body.replace(/-/g, "+").replace(/_/g, "/")
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    )
    const json = typeof atob === "function" ? atob(padded) : ""
    const parsed = JSON.parse(json) as { role?: unknown }
    const raw = parsed.role
    return typeof raw === "number" && Number.isInteger(raw) ? raw : 3
  } catch {
    return 3
  }
}

/**
 * Rede de segurança para erros de token expirado que escapem ao client (que já
 * renova e repete o pedido sozinho): pede um token novo ao CRM, SEM recarregar
 * — o reload reabria a iframe com o mesmo token velho do URL e perdia o
 * trabalho. Throttle 10s para não inundar o host.
 */
let _lastTokenReloadAt = 0
function refreshOnTokenExpiry(error: unknown, session: AppSession): boolean {
  const msg = String(
    error instanceof Error ? error.message : (error as { message?: string })?.message ?? error,
  ).toLowerCase()
  if (
    !msg.includes("token expired") &&
    !msg.includes("invalid app shell token") &&
    !msg.includes("missing app token")
  ) {
    return false
  }
  const now = Date.now()
  if (now - _lastTokenReloadAt < 10_000) return false
  _lastTokenReloadAt = now
  console.warn("[maisenergia] Token expirado — a pedir um token novo ao CRM.")
  void session.refresh()
  return true
}

function useAppShell(): AppShell | null {
  const [shell, setShell] = useState<AppShell | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get("token")
    if (!token) {
      console.warn("[maisenergia] No token in URL — running standalone (dev).")
      return
    }

    // Sessão do SDK: o CRM renova o token (30 min) por postMessage e o client
    // — ESTÁVEL, envia sempre o token mais recente — troca-o sem recarregar.
    // Como a identidade do `shell` não muda, nenhum useEffect([shell]) volta a
    // correr (senão o Editar recarregava a proposta por cima do que estava a
    // ser escrito). Um 401 "token expired" é repetido uma vez após renovar.
    let session: AppSession
    try {
      session = createAppSession(token)
    } catch (err) {
      console.error("[maisenergia] Invalid token:", err)
      return
    }
    const bridge = createBridge({
      allowedCapabilities: [
        "toast",
        "navigate.crm",
        "open.preview",
        "files.upload",
        "request.action",
      ],
    })

    // Global handlers para apanhar erros de token expirado em qualquer
    // chamada (incluindo as que bypassam o unwrap helper).
    const onRejection = (e: PromiseRejectionEvent) =>
      refreshOnTokenExpiry(e.reason, session)
    const onError = (e: ErrorEvent) =>
      refreshOnTokenExpiry(e.error ?? e.message, session)
    window.addEventListener("unhandledrejection", onRejection)
    window.addEventListener("error", onError)

    const baseShell = {
      context: session.context,
      client: session.client,
      bridge,
      role: decodeRoleFromToken(token),
    }
    setShell(baseShell)
    // Resolve o admin por CARGO (user_roles) — o role numérico do token não
    // reflete os Cargos geridos na UI. Falha silenciosa → isAdmin fica false.
    void fetchMe(baseShell)
      .then((me) =>
        setShell((s) =>
          s
            ? {
                ...s,
                isAdmin: me.is_admin,
                canDiscount: me.can_discount,
                areas: me.areas?.length ? me.areas : ["simulador"],
                teams: me.teams ?? [],
              }
            : s,
        ),
      )
      .catch(() =>
        setShell((s) =>
          s
            ? { ...s, isAdmin: false, canDiscount: false, areas: ["simulador"], teams: [] }
            : s,
        ),
      )

    return () => {
      window.removeEventListener("unhandledrejection", onRejection)
      window.removeEventListener("error", onError)
      session.dispose()
    }
  }, [])

  return shell
}

export function App() {
  const shell = useAppShell()
  useHostDeepLink(shell)
  const { pathname } = useLocation()
  // A Produção precisa da largura toda (quadro de 5 colunas a partir de 1200 px).
  const wide = areaOfPath(pathname) === "producao"
  // O Armazém é usado sobretudo no telemóvel: margens curtas para o ecrã todo.
  const armazem = areaOfPath(pathname) === "armazem"
  const sim = (el: ReactNode) => (
    <RequireArea shell={shell} area="simulador">
      {el}
    </RequireArea>
  )

  return (
    <div className="min-h-screen w-full bg-background text-foreground">
      <AppHeader shell={shell} />
      <main
        className={
          wide
            ? "w-full px-4 py-6 sm:px-6"
            : armazem
              ? "mx-auto max-w-[1600px] px-3 py-3 sm:px-6 sm:py-6 lg:px-8"
              : "mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8"
        }
      >
        <Routes>
          {/* Menu inicial (o iframe abre em /<app>/<versão>/index.html → "*"). */}
          <Route path="/" element={<Launcher shell={shell} />} />
          <Route path="/index.html" element={<Launcher shell={shell} />} />

          {/* ── Simulador — as rotas antigas não mudam (ações e PDFs usam-nas). */}
          <Route path="/simulador" element={sim(<Home shell={shell} />)} />
          <Route path="/list" element={sim(<List shell={shell} />)} />
          <Route
            path="/configuracoes"
            element={
              <RequireRole shell={shell} min={MANAGE_MIN_ROLE}>
                <Settings shell={shell} />
              </RequireRole>
            }
          />
          <Route
            path="/mapeamentos"
            element={
              <RequireRole shell={shell} min={MANAGE_MIN_ROLE}>
                <Mapeamentos shell={shell} />
              </RequireRole>
            }
          />
          <Route path="/ajuda" element={<Ajuda />} />

          {/* Solar */}
          <Route path="/solar/criar" element={sim(<Wizard shell={shell} />)} />
          <Route path="/solar/:id" element={sim(<Detail shell={shell} />)} />
          <Route path="/solar/:id/editar" element={sim(<Edit shell={shell} />)} />

          {/* BC AQS / BC AQ — engines activos (criação + detalhe) */}
          <Route path="/bc-aqs/criar" element={sim(<BcWizard shell={shell} kind="bc_aqs" />)} />
          <Route path="/bc-aqs/:id" element={sim(<BcDetail shell={shell} kind="bc_aqs" />)} />
          <Route path="/bc-aqs/:id/editar" element={sim(<BcWizard shell={shell} kind="bc_aqs" />)} />
          <Route path="/bc-aq/criar" element={sim(<BcWizard shell={shell} kind="bc_aq" />)} />
          <Route path="/bc-aq/:id" element={sim(<BcDetail shell={shell} kind="bc_aq" />)} />
          <Route path="/bc-aq/:id/editar" element={sim(<BcWizard shell={shell} kind="bc_aq" />)} />

          {/* AC — engine activo (criação + detalhe + edição) */}
          <Route path="/ac/criar" element={sim(<AcWizard shell={shell} />)} />
          <Route path="/ac/:id" element={sim(<AcDetail shell={shell} />)} />
          <Route path="/ac/:id/editar" element={sim(<AcWizard shell={shell} />)} />

          {/* ── Produção (janela do gestor) — rotas internas na própria área. */}
          <Route
            path="/producao/*"
            element={
              <RequireArea shell={shell} area="producao">
                <ProducaoArea shell={shell} />
              </RequireArea>
            }
          />

          {/* ── «Abrir na app» da ficha do contrato no Koortex → a obra desse contrato. */}
          <Route
            path="/contracts/:contractId"
            element={
              <RequireArea shell={shell} area="producao">
                <ContractLink shell={shell} />
              </RequireArea>
            }
          />

          {/* ── Armazém — a Fase 2 enche src/areas/armazem. */}
          <Route
            path="/armazem/*"
            element={
              <RequireArea shell={shell} area="armazem">
                <ArmazemArea shell={shell} />
              </RequireArea>
            }
          />

          {/* ── Relatórios (gestores) — propostas por mês, vendedor, estado, origem. */}
          <Route
            path="/relatorios"
            element={
              <RequireArea shell={shell} area="reporting">
                <Reporting shell={shell} />
              </RequireArea>
            }
          />

          <Route path="*" element={<Launcher shell={shell} />} />
        </Routes>
      </main>
    </div>
  )
}
